import React, { createContext, useContext, useState, useCallback } from 'react';
import Confetti from 'react-confetti';
import { useWindowSize } from '@/hooks/useWindowSize';

export type CelebrationLevel = "small" | "medium" | "large" | "epic";

interface ConfettiContextType {
  triggerCelebration: (level?: CelebrationLevel) => void;
}

const ConfettiContext = createContext<ConfettiContextType | undefined>(undefined);

export function ConfettiProvider({ children }: { children: React.ReactNode }) {
  const [isActive, setIsActive] = useState(false);
  const [config, setConfig] = useState({ pieces: 200, duration: 3000 });
  const { width, height } = useWindowSize();

  const triggerCelebration = useCallback((level: CelebrationLevel = "medium") => {
    // Check if confetti is globally enabled
    const isEnabled = localStorage.getItem('confetti_enabled') !== 'false';
    if (!isEnabled) {
      return;
    }

    const configs = {
      small: { pieces: 100, duration: 2000 },
      medium: { pieces: 200, duration: 3000 },
      large: { pieces: 300, duration: 4000 },
      epic: { pieces: 500, duration: 5000 },
    };

    setConfig(configs[level]);
    setIsActive(true);

    setTimeout(() => {
      setIsActive(false);
    }, configs[level].duration);
  }, []);

  return (
    <ConfettiContext.Provider value={{ triggerCelebration }}>
      {children}
      {isActive && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 9999 }}>
          <Confetti
            width={width}
            height={height}
            numberOfPieces={config.pieces}
            recycle={false}
            gravity={0.3}
            colors={['#4169B8', '#2E4A92', '#60A5FA', '#34D399', '#FBBF24', '#FFD700']}
          />
        </div>
      )}
    </ConfettiContext.Provider>
  );
}

export function useConfetti() {
  const context = useContext(ConfettiContext);
  if (!context) {
    throw new Error('useConfetti must be used within ConfettiProvider');
  }
  return context;
}
